import { useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CircleAlert, CircleCheck } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import {
  CUSTOMER_RATING,
  LATE_PAYMENT_SEVERITY,
  PAYMENT_PROMPTNESS,
} from '@/lib/api-types'
import { formatDate } from '@/lib/format'
import type { FieldErrors } from '@/lib/zod-errors'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, FieldError, FieldHint, MicroLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { RadioGroup } from '@/components/ui/radio-group'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { Wordmark } from '@/components/wordmark'
import {
  buildReferencePayload,
  emptyAnswers,
  initialReferee,
  validateReference,
  type AnswersFormState,
  type RefereeFormState,
} from './reference-schema'

/**
 * /reference/:token — the no-login page a trade referee's emailed link lands
 * on (outside AuthGate; the token IS the authorization). Error states stay
 * generic on purpose — an expired or forged link never names anyone.
 */

const LINK_ERROR_COPY = {
  expired: {
    heading: 'This link has expired.',
    body: 'Reference links only work for a limited time. Ask the company that requested the reference to send a new one — it takes them one click.',
  },
  invalid: {
    heading: 'This link isn’t valid.',
    body: 'It may have been trimmed by your email client. Open the original email and use the full link.',
  },
} as const

export function TradeReferencePage() {
  const { token = '' } = useParams()
  const [outcome, setOutcome] = useState<'sent' | 'already' | null>(null)

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['trade-reference', token],
    queryFn: ({ signal }) => api.publicGetTradeReference(token, { signal }),
    // 404/410 are terminal answers, not flakes — surface them immediately.
    retry: false,
    staleTime: Infinity,
  })

  let content: ReactNode
  if (outcome === 'sent') {
    content = (
      <TerminalCard
        icon={<CircleCheck aria-hidden="true" className="size-6 text-brand" />}
        heading="Reference sent"
        body="Thank you — your answers went straight to Green Line’s credit team. You can close this page."
      />
    )
  } else if (outcome === 'already' || (data && data.status === 'completed')) {
    content = (
      <TerminalCard
        icon={<CircleCheck aria-hidden="true" className="size-6 text-brand" />}
        heading="This reference was already completed — thank you."
        body="Your answers are with Green Line’s credit team. Nothing else is needed."
      />
    )
  } else if (isPending) {
    content = (
      <div className="flex justify-center pt-[10vh]">
        <Spinner label="Opening the reference form…" />
      </div>
    )
  } else if (isError) {
    const kind =
      error instanceof ApiError && error.status === 410 ? 'expired' : 'invalid'
    content = (
      <TerminalCard
        icon={<CircleAlert aria-hidden="true" className="size-6 text-rust" />}
        heading={LINK_ERROR_COPY[kind].heading}
        body={LINK_ERROR_COPY[kind].body}
      />
    )
  } else {
    content = (
      <ReferenceForm
        token={token}
        customerName={data.customerName}
        refereePrefill={data.refereePrefill}
        onDone={setOutcome}
      />
    )
  }

  return (
    <div className="flex min-h-screen flex-col bg-snow">
      <header className="mx-auto flex h-14 w-full max-w-5xl items-center px-6">
        <Wordmark />
      </header>
      <main className="mx-auto w-full max-w-form flex-1 px-6 pb-24 pt-8">{content}</main>
    </div>
  )
}

function TerminalCard({
  icon,
  heading,
  body,
}: {
  icon: ReactNode
  heading: string
  body: string
}) {
  return (
    <div className="flex justify-center pt-[8vh]">
      <Card padding="none" className="w-full max-w-md p-8">
        {icon}
        <h1 className="mt-4 text-xl font-semibold tracking-wordmark text-ink">{heading}</h1>
        <p className="mt-2 text-sm leading-relaxed text-inkMid">{body}</p>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The open form
// ---------------------------------------------------------------------------

function ReferenceForm({
  token,
  customerName,
  refereePrefill,
  onDone,
}: {
  token: string
  customerName: string
  refereePrefill: Parameters<typeof initialReferee>[0]
  onDone: (outcome: 'sent' | 'already') => void
}) {
  const [referee, setReferee] = useState<RefereeFormState>(() => initialReferee(refereePrefill))
  const [answers, setAnswers] = useState<AnswersFormState>(emptyAnswers)
  const [attempted, setAttempted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const errors: FieldErrors = attempted ? validateReference(referee, answers) : {}

  const mutation = useMutation({
    mutationFn: () => api.publicSubmitTradeReference(token, buildReferencePayload(referee, answers)),
    onSuccess: () => {
      onDone('sent')
      window.scrollTo({ top: 0 })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        onDone('already')
        return
      }
      setSubmitError('That didn’t send. Your answers are still here — try again.')
    },
  })

  function send() {
    if (Object.keys(validateReference(referee, answers)).length > 0) {
      setAttempted(true)
      return
    }
    setSubmitError(null)
    mutation.mutate()
  }

  function patchReferee(changes: Partial<RefereeFormState>) {
    setReferee((current) => ({ ...current, ...changes }))
  }

  function patchAnswers(changes: Partial<AnswersFormState>) {
    setAnswers((current) => ({ ...current, ...changes }))
  }

  return (
    <div>
      <p className="micro-label">Trade reference request</p>
      <h1 className="mt-1 text-[1.375rem] font-semibold tracking-wordmark text-ink">
        Trade reference for {customerName}
      </h1>
      <p className="mt-2 max-w-xl text-sm leading-relaxed text-mute">
        {customerName} named your company as a trade reference on a credit application with Green
        Line Ingredients. Seven quick questions — no account needed, and your answers go only to
        Green Line&rsquo;s credit team.
      </p>

      <section aria-labelledby="referee-heading" className="mt-8">
        <SectionHeader id="referee-heading" title="Your details" />
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <RefField
            id="referee-name"
            label="Your name"
            value={referee.name}
            onChange={(name) => patchReferee({ name })}
            error={errors['referee.name']}
            autoComplete="name"
          />
          <RefField
            id="referee-title"
            label="Your title"
            value={referee.title}
            onChange={(title) => patchReferee({ title })}
            error={errors['referee.title']}
            autoComplete="organization-title"
          />
          <RefField
            id="referee-company"
            label="Company"
            value={referee.company}
            onChange={(company) => patchReferee({ company })}
            error={errors['referee.company']}
            autoComplete="organization"
          />
          <RefField
            id="referee-email"
            label="Email"
            value={referee.email}
            onChange={(email) => patchReferee({ email })}
            error={errors['referee.email']}
            type="email"
            autoComplete="email"
          />
          <RefField
            id="referee-phone"
            label="Phone"
            value={referee.phone}
            onChange={(phone) => patchReferee({ phone })}
            error={errors['referee.phone']}
            type="tel"
            hint="Optional."
          />
          <RefField
            id="referee-address"
            label="Address"
            value={referee.address}
            onChange={(address) => patchReferee({ address })}
            error={errors['referee.address']}
            hint="Optional."
          />
        </div>
      </section>

      <section aria-labelledby="questions-heading" className="mt-10">
        <SectionHeader id="questions-heading" title="About their account" />
        <div className="mt-4 space-y-7">
          <RefField
            id="q-account-length"
            label={`How long has ${customerName} had an account with you?`}
            value={answers.accountLength}
            onChange={(accountLength) => patchAnswers({ accountLength })}
            error={errors['accountLength']}
            placeholder="e.g. 4 years"
          />
          <RefField
            id="q-turnover"
            label="Average monthly purchases (USD)"
            value={answers.averageMonthlyTurnover}
            onChange={(averageMonthlyTurnover) => patchAnswers({ averageMonthlyTurnover })}
            error={errors['averageMonthlyTurnover']}
            type="number"
            hint="An approximate amount is fine. Optional."
          />
          <RefField
            id="q-credit-terms"
            label="What credit terms do you extend to them?"
            value={answers.creditTerms}
            onChange={(creditTerms) => patchAnswers({ creditTerms })}
            error={errors['creditTerms']}
            placeholder="e.g. Net 30"
          />

          <RadioQuestion
            id="q-promptness"
            label="How promptly do they pay?"
            options={PAYMENT_PROMPTNESS}
            value={answers.paymentPromptness}
            onChange={(paymentPromptness) => patchAnswers({ paymentPromptness })}
            error={errors['paymentPromptness']}
          />
          <RadioQuestion
            id="q-late-severity"
            label="When payments run late, how late do they run?"
            options={LATE_PAYMENT_SEVERITY}
            value={answers.latePaymentSeverity}
            onChange={(latePaymentSeverity) => patchAnswers({ latePaymentSeverity })}
            error={errors['latePaymentSeverity']}
          />
          <RadioQuestion
            id="q-rating"
            label="How would you rate them as a customer?"
            options={CUSTOMER_RATING}
            value={answers.customerRating}
            onChange={(customerRating) => patchAnswers({ customerRating })}
            error={errors['customerRating']}
          />

          <Field>
            <MicroLabel htmlFor="q-comments">Anything else Green Line should know?</MicroLabel>
            <Textarea
              id="q-comments"
              rows={3}
              value={answers.comments}
              onChange={(event) => patchAnswers({ comments: event.target.value })}
              aria-describedby="q-comments-hint"
            />
            <FieldHint id="q-comments-hint">Optional.</FieldHint>
          </Field>
        </div>
      </section>

      <div className="mt-10 border-t border-rule/50 pt-6">
        <p className="text-sm leading-relaxed text-mute">
          Sending this form signs the reference as the contact named above ·{' '}
          {formatDate(new Date().toISOString())}.
        </p>
        {attempted && Object.keys(errors).length > 0 ? (
          <p role="alert" className="mt-3 text-sm font-medium text-rust">
            Fix the highlighted fields to send your reference.
          </p>
        ) : null}
        {submitError ? (
          <p role="alert" className="mt-3 rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-sm text-rust">
            {submitError}
          </p>
        ) : null}
        <Button
          type="button"
          size="lg"
          className="mt-5"
          loading={mutation.isPending}
          onClick={send}
        >
          {mutation.isPending ? 'Sending…' : 'Send reference'}
        </Button>
      </div>
    </div>
  )
}

function RefField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  type = 'text',
  placeholder,
  autoComplete,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  hint?: string
  type?: string
  placeholder?: string
  autoComplete?: string
}) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined
  return (
    <Field>
      <MicroLabel htmlFor={id}>{label}</MicroLabel>
      <Input
        id={id}
        type={type}
        min={type === 'number' ? 0 : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </Field>
  )
}

function RadioQuestion({
  id,
  label,
  options,
  value,
  onChange,
  error,
}: {
  id: string
  label: string
  options: readonly string[]
  value: string
  onChange: (value: string) => void
  error?: string
}) {
  const labelId = `${id}-label`
  const errorId = error ? `${id}-error` : undefined
  return (
    <Field>
      <span id={labelId} className="micro-label block">
        {label}
      </span>
      <RadioGroup
        aria-labelledby={labelId}
        value={value || null}
        onValueChange={onChange}
        options={options.map((option) => ({ value: option, label: option }))}
      />
      {error ? <FieldError id={errorId}>Choose one.</FieldError> : null}
    </Field>
  )
}
